import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Calendar, Clock, LogOut, CheckCircle2, 
  AlertTriangle, Wrench, MessageSquare, Camera, FileText, 
  Upload, X, ChevronRight, Home, ClipboardList, User, RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// API Functions
const api = {
  async login(email, password) {
    const res = await axios.post(`${API}/api/contractor/login`, { email, password });
    return res.data;
  },
  async getDashboard(token) {
    const res = await axios.get(`${API}/api/contractor/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async getJobs(token, status = '') {
    const url = status ? `${API}/api/contractor/jobs?status=${encodeURIComponent(status)}` : `${API}/api/contractor/jobs`;
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async getJob(token, jobId) {
    const res = await axios.get(`${API}/api/contractor/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async updateStatus(token, jobId, contractor_status) {
    const res = await axios.patch(`${API}/api/contractor/jobs/${jobId}/status`, 
      { contractor_status },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    return res.data;
  },
  async updateSchedule(token, jobId, scheduled_visit_date, attendance_confirmed = false) {
    const res = await axios.patch(`${API}/api/contractor/jobs/${jobId}/schedule`,
      { scheduled_visit_date, attendance_confirmed },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    return res.data;
  },
  async addNote(token, jobId, message, note_type = 'General Update') {
    const res = await axios.post(`${API}/api/contractor/jobs/${jobId}/notes`,
      { message, note_type },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    return res.data;
  },
  async uploadFile(token, jobId, file, file_category, caption = '') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_category', file_category);
    formData.append('caption', caption);

    const res = await axios.post(`${API}/api/contractor/jobs/${jobId}/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  },
  async submitInvoice(token, jobId, invoiceData) {
    const res = await axios.post(`${API}/api/contractor/jobs/${jobId}/invoice`, invoiceData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    });
    return res.data;
  },
  async getProfile(token) {
    const res = await axios.get(`${API}/api/contractor/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return res.data;
  }
};

// KPI Card Component
function StatCard({ title, value, icon: Icon, tone = 'default' }) {
  const toneClasses = {
    default: 'border-slate-200 bg-white',
    urgent: 'border-red-200 bg-red-50',
    active: 'border-blue-200 bg-blue-50',
    done: 'border-green-200 bg-green-50',
    warn: 'border-amber-200 bg-amber-50',
  };

  return (
    <div className={`rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${toneClasses[tone]}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
        </div>
        <div className="rounded-xl bg-slate-100 p-3">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

// Job Card Component
function JobCard({ job, onOpen, onAccept, onDecline, onComplete }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New Request': 'bg-amber-100 text-amber-800 border-amber-200',
      'Accepted': 'bg-blue-100 text-blue-800 border-blue-200',
      'Scheduled': 'bg-purple-100 text-purple-800 border-purple-200',
      'In Progress': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'Awaiting Materials': 'bg-orange-100 text-orange-800 border-orange-200',
      'Completed': 'bg-green-100 text-green-800 border-green-200',
      'Declined': 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
  };

  return (
    <div 
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-[#1F7A78]/30 transition-all cursor-pointer" 
      data-testid={`job-card-${job.id}`}
      onClick={() => onOpen(job)}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-bold text-[#1F7A78]">{job.job_number || job.id}</p>
            <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />
              {job.property_address || job.site_name || job.park_name || 'Address not set'}
              {job.plot_number && <span className="text-slate-500">• Plot {job.plot_number}</span>}
            </p>
          </div>
          <Badge className={`${getStatusColor(job.contractor_status)} border`}>
            {job.contractor_status || 'New Request'}
          </Badge>
        </div>
        
        <p className="text-sm text-slate-700 line-clamp-2">
          {job.scope_summary || job.description || job.title || 'No description'}
        </p>

        <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>Visit: {formatDate(job.scheduled_visit_date)}</span>
          </div>
          {job.priority && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              <span>Priority: {job.priority}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
          <Button 
            size="sm" 
            onClick={(e) => { e.stopPropagation(); onOpen(job); }}
            className="bg-[#1F7A78] hover:bg-[#005456] text-white"
            data-testid={`view-job-${job.id}`}
          >
            View Job
          </Button>
          {(!job.contractor_status || job.contractor_status === 'New Request') && (
            <>
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onAccept(job); }}
                className="border-green-500 text-green-700 hover:bg-green-50"
                data-testid={`accept-job-${job.id}`}
              >
                Accept
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onDecline(job); }}
                className="border-red-500 text-red-700 hover:bg-red-50"
                data-testid={`decline-job-${job.id}`}
              >
                Decline
              </Button>
            </>
          )}
          {job.contractor_status === 'In Progress' && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onComplete(job); }}
              className="border-green-500 text-green-700 hover:bg-green-50"
              data-testid={`complete-job-${job.id}`}
            >
              Mark Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Notes Panel Component
function NotesPanel({ notes, onAdd, loading }) {
  const [message, setMessage] = useState('');
  const [noteType, setNoteType] = useState('General Update');
  const [submitting, setSubmitting] = useState(false);

  const noteTypes = [
    'General Update',
    'Attendance Update',
    'Site Issue',
    'Materials Required',
    'Access Issue',
    'Completion Update'
  ];

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await onAdd(message, noteType);
      setMessage('');
      setNoteType('General Update');
      toast.success('Note added successfully');
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-[#1F7A78]" />
        <h3 className="text-lg font-semibold text-slate-900">Live Notes</h3>
      </div>

      <div className="mb-4 space-y-3 max-h-64 overflow-auto">
        {loading ? (
          <div className="text-center py-4 text-slate-500">Loading notes...</div>
        ) : notes.length === 0 ? (
          <div className="text-center py-4 text-slate-500">No notes yet</div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="rounded-xl bg-slate-50 p-3 border-l-4 border-[#1F7A78]">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-medium text-slate-900">
                  {note.created_by_name} 
                  <span className="text-slate-500 font-normal ml-1">({note.created_by_role})</span>
                </p>
                <p className="text-xs text-slate-500">{formatDate(note.created_at)}</p>
              </div>
              <Badge variant="outline" className="text-xs mb-2 bg-amber-50 text-amber-700 border-amber-200">
                {note.note_type}
              </Badge>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.message}</p>
            </div>
          ))
        )}
      </div>

      <div className="space-y-3 pt-3 border-t border-slate-100">
        <select 
          value={noteType} 
          onChange={(e) => setNoteType(e.target.value)} 
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#1F7A78] focus:border-[#1F7A78]"
          data-testid="note-type-select"
        >
          {noteTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <Textarea 
          value={message} 
          onChange={(e) => setMessage(e.target.value)} 
          rows={3} 
          className="rounded-xl border-slate-300 focus:ring-2 focus:ring-[#1F7A78] focus:border-[#1F7A78]" 
          placeholder="Add a note for staff and contractor view..."
          data-testid="note-message-input"
        />
        <Button
          onClick={handleSubmit}
          disabled={!message.trim() || submitting}
          className="w-full bg-[#1F7A78] hover:bg-[#005456]"
          data-testid="add-note-btn"
        >
          {submitting ? 'Adding...' : 'Add Note'}
        </Button>
      </div>
    </div>
  );
}

// Upload Panel Component
function UploadPanel({ onUpload, files = [] }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState('Site Photo');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const categories = [
    'Site Photo',
    'Before Photo',
    'During Works Photo',
    'Completion Photo',
    'Certificate',
    'Completion Sheet',
    'Other Document'
  ];

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file, category, caption);
      setFile(null);
      setCaption('');
      setCategory('Site Photo');
      toast.success('File uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Camera className="h-5 w-5 text-[#1F7A78]" />
        <h3 className="text-lg font-semibold text-slate-900">Uploads</h3>
      </div>

      {/* Existing files */}
      {files.length > 0 && (
        <div className="mb-4 space-y-2 max-h-32 overflow-auto">
          {files.map((f) => (
            <div key={f.id} className="flex items-center justify-between rounded-lg bg-slate-50 p-2 text-sm">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="text-slate-700 truncate max-w-[150px]">{f.file_name}</span>
              </div>
              <Badge variant="outline" className="text-xs">{f.file_category}</Badge>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <select 
          value={category} 
          onChange={(e) => setCategory(e.target.value)} 
          className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#1F7A78]"
          data-testid="upload-category-select"
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input 
          type="file" 
          onChange={(e) => setFile(e.target.files?.[0] || null)} 
          className="rounded-xl border-slate-300"
          data-testid="file-input"
        />
        <Input 
          value={caption} 
          onChange={(e) => setCaption(e.target.value)} 
          className="rounded-xl border-slate-300" 
          placeholder="Caption / description (optional)"
          data-testid="file-caption-input"
        />
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="upload-file-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      </div>
    </div>
  );
}

// Schedule Panel Component
function SchedulePanel({ scheduledVisitDate, onSave }) {
  const [value, setValue] = useState(scheduledVisitDate || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value) return;
    setSaving(true);
    try {
      await onSave(value);
      toast.success('Visit date updated');
    } catch (err) {
      toast.error('Failed to update visit date');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Calendar className="h-5 w-5 text-[#1F7A78]" />
        <h3 className="text-lg font-semibold text-slate-900">Scheduled Visit</h3>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Input 
          type="datetime-local" 
          value={value} 
          onChange={(e) => setValue(e.target.value)} 
          className="flex-1 rounded-xl border-slate-300"
          data-testid="visit-date-input"
        />
        <Button 
          onClick={handleSave}
          disabled={saving || !value}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="set-visit-date-btn"
        >
          {saving ? 'Saving...' : 'Set Visit Date'}
        </Button>
      </div>
    </div>
  );
}

// Job Detail Panel Component
function JobDetailPanel({ job, notes, files, updates, onStatusChange, onSchedule, onAddNote, onUpload, onInvoice, onClose }) {
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [submittingInvoice, setSubmittingInvoice] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New Request': 'bg-amber-100 text-amber-800',
      'Accepted': 'bg-blue-100 text-blue-800',
      'Scheduled': 'bg-purple-100 text-purple-800',
      'In Progress': 'bg-cyan-100 text-cyan-800',
      'Awaiting Materials': 'bg-orange-100 text-orange-800',
      'Completed': 'bg-green-100 text-green-800',
      'Declined': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="space-y-4" data-testid="job-detail-panel">
      {/* Job Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-[#1F7A78]">{job.job_number || job.id}</h2>
              <Button variant="ghost" size="sm" onClick={onClose} className="ml-auto sm:hidden">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-slate-600 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {job.property_address || 'Address not set'}
              {job.property_postcode && `, ${job.property_postcode}`}
            </p>
            {job.plot_number && (
              <p className="text-sm text-slate-500">Plot: {job.plot_number}</p>
            )}
          </div>
          <Badge className={`${getStatusColor(job.contractor_status)} shrink-0`}>
            {job.contractor_status || 'New Request'}
          </Badge>
        </div>

        <div className="bg-slate-50 rounded-xl p-3 mb-4">
          <p className="text-sm font-medium text-slate-900 mb-1">Scope / Description</p>
          <p className="text-sm text-slate-700">
            {job.scope_summary || job.description || job.title || 'No description provided'}
          </p>
        </div>

        {/* LHS Notes / Instructions for Contractor */}
        {(job.notes || job.access_notes) && (
          <div className="bg-[#1F7A78]/10 rounded-xl p-3 mb-4 border border-[#1F7A78]/20">
            <p className="text-sm font-medium text-[#1F7A78] mb-1 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Instructions from LHS
            </p>
            {job.notes && (
              <p className="text-sm text-slate-700 mb-1">{job.notes}</p>
            )}
            {job.access_notes && (
              <p className="text-sm text-slate-600"><span className="font-medium">Access:</span> {job.access_notes}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div>
            <span className="text-slate-500">Job Type:</span>
            <span className="ml-2 text-slate-900">{job.job_type || job.damage_type || 'General'}</span>
          </div>
          <div>
            <span className="text-slate-500">Priority:</span>
            <span className="ml-2 text-slate-900">{job.priority || 'Medium'}</span>
          </div>
          <div>
            <span className="text-slate-500">Scheduled:</span>
            <span className="ml-2 text-slate-900">{formatDate(job.scheduled_visit_date)}</span>
          </div>
          <div>
            <span className="text-slate-500">Last Updated:</span>
            <span className="ml-2 text-slate-900">{formatDate(job.updated_at)}</span>
          </div>
        </div>

        {/* Status Actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          {(!job.contractor_status || job.contractor_status === 'New Request') && (
            <>
              <Button 
                size="sm" 
                onClick={() => onStatusChange('Accepted')}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="accept-job-btn"
              >
                Accept Job
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onStatusChange('Declined')}
                className="border-red-500 text-red-700 hover:bg-red-50"
                data-testid="decline-job-btn"
              >
                Decline Job
              </Button>
            </>
          )}
          {job.contractor_status === 'Accepted' && (
            <Button 
              size="sm" 
              onClick={() => onStatusChange('In Progress')}
              className="bg-[#1F7A78] hover:bg-[#005456]"
              data-testid="start-job-btn"
            >
              Mark In Progress
            </Button>
          )}
          {(job.contractor_status === 'Scheduled' || job.contractor_status === 'Accepted') && (
            <Button 
              size="sm" 
              onClick={() => onStatusChange('In Progress')}
              className="bg-[#1F7A78] hover:bg-[#005456]"
              data-testid="start-work-btn"
            >
              Start Work
            </Button>
          )}
          {job.contractor_status === 'In Progress' && (
            <>
              <Button 
                size="sm" 
                onClick={() => onStatusChange('Awaiting Materials')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                data-testid="awaiting-materials-btn"
              >
                Awaiting Materials
              </Button>
              <Button 
                size="sm" 
                onClick={() => onStatusChange('Completed')}
                className="bg-green-600 hover:bg-green-700"
                data-testid="complete-job-btn"
              >
                Mark Completed
              </Button>
            </>
          )}
          {job.contractor_status === 'Awaiting Materials' && (
            <>
              <Button 
                size="sm" 
                onClick={() => onStatusChange('In Progress')}
                className="bg-[#1F7A78] hover:bg-[#005456]"
                data-testid="resume-work-btn"
              >
                Resume Work
              </Button>
              <Button 
                size="sm" 
                onClick={() => onStatusChange('Completed')}
                className="bg-green-600 hover:bg-green-700"
                data-testid="complete-job-btn-2"
              >
                Mark Completed
              </Button>
            </>
          )}
          {/* Invoice Button - shown for completed jobs */}
          {(job.contractor_status === 'Completed' || job.contractor_status === 'Work Complete') && !job.contractor_invoice_id && (
            <Button 
              size="sm" 
              onClick={() => setShowInvoiceForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="invoice-job-btn"
            >
              <FileText className="h-4 w-4 mr-1" />
              Invoice Job
            </Button>
          )}
          {job.contractor_invoice_id && (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Invoice Submitted
            </Badge>
          )}
        </div>
      </div>

      {/* Invoice Form Modal */}
      {showInvoiceForm && (
        <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Submit Invoice
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowInvoiceForm(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invoice-number" className="text-sm font-medium text-slate-700">
                Your Invoice Number (optional)
              </Label>
              <Input
                id="invoice-number"
                placeholder="e.g., INV-2024-001"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="invoice-amount" className="text-sm font-medium text-slate-700">
                Amount (£ ex VAT) *
              </Label>
              <Input
                id="invoice-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invoiceAmount}
                onChange={(e) => setInvoiceAmount(e.target.value)}
                className="mt-1"
                required
              />
            </div>
            <div className="bg-white rounded-lg p-3 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Amount (ex VAT):</span>
                <span>£{parseFloat(invoiceAmount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>VAT (20%):</span>
                <span>£{(parseFloat(invoiceAmount || 0) * 0.2).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-slate-900 pt-2 border-t mt-2">
                <span>Total (inc VAT):</span>
                <span>£{(parseFloat(invoiceAmount || 0) * 1.2).toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label htmlFor="invoice-notes" className="text-sm font-medium text-slate-700">
                Notes (optional)
              </Label>
              <Textarea
                id="invoice-notes"
                placeholder="Any additional notes..."
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
                    toast.error('Please enter a valid amount');
                    return;
                  }
                  setSubmittingInvoice(true);
                  try {
                    await onInvoice({
                      amount: parseFloat(invoiceAmount),
                      invoice_number: invoiceNumber,
                      notes: invoiceNotes
                    });
                    setShowInvoiceForm(false);
                    setInvoiceAmount('');
                    setInvoiceNumber('');
                    setInvoiceNotes('');
                  } finally {
                    setSubmittingInvoice(false);
                  }
                }}
                disabled={submittingInvoice || !invoiceAmount}
                className="bg-indigo-600 hover:bg-indigo-700 flex-1"
                data-testid="submit-invoice-btn"
              >
                {submittingInvoice ? 'Submitting...' : 'Submit Invoice'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowInvoiceForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Panel */}
      <SchedulePanel 
        scheduledVisitDate={job.scheduled_visit_date} 
        onSave={onSchedule} 
      />

      {/* Notes Panel */}
      <NotesPanel 
        notes={notes} 
        onAdd={onAddNote} 
        loading={false}
      />

      {/* Upload Panel */}
      <UploadPanel 
        onUpload={onUpload}
        files={files}
      />

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#1F7A78]" />
            <h3 className="text-lg font-semibold text-slate-900">Uploaded Files ({files.length})</h3>
          </div>
          <div className="space-y-2 max-h-48 overflow-auto">
            {files.map((file) => (
              <div key={file.id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-sm hover:bg-slate-100 transition-colors">
                <div>
                  <p className="font-medium text-slate-900">{file.file_name}</p>
                  <p className="text-slate-500 text-xs">
                    {file.file_category}
                    {file.caption && ` • ${file.caption}`}
                  </p>
                </div>
                <a 
                  href={`${API}${file.file_url}`} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Timeline */}
      {updates.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#1F7A78]" />
            <h3 className="text-lg font-semibold text-slate-900">Activity Timeline</h3>
          </div>
          <div className="space-y-3 max-h-48 overflow-auto">
            {updates.map((update) => (
              <div key={update.id} className="flex gap-3 text-sm border-l-2 border-slate-200 pl-3">
                <div className="flex-1">
                  <p className="text-slate-900">
                    <span className="font-medium">{update.update_type.replace(/_/g, ' ')}</span>
                    {update.old_value && update.new_value && (
                      <span className="text-slate-500"> • {update.old_value} → {update.new_value}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500">
                    {update.changed_by_name} • {formatDate(update.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Contractor Portal Page
export default function ContractorPortalPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [allJobs, setAllJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // New tab structure for job filtering
  const [jobsSubTab, setJobsSubTab] = useState('reactive');
  
  // Job filter tabs configuration
  const JOB_TABS = [
    { key: "reactive", label: "🔥 Reactive", icon: AlertTriangle },
    { key: "ppm", label: "🔁 PPM", icon: Calendar },
    { key: "quoted", label: "💰 Quoted Work", icon: FileText },
    { key: "scheduled", label: "📍 My Day", icon: MapPin },
    { key: "action", label: "⚠️ Awaiting Action", icon: Clock },
    { key: "invoicing", label: "📄 Invoicing", icon: FileText }
  ];
  
  // Track if we're on mobile (for Dialog rendering)
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1280); // xl breakpoint
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check stored auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('contractor_token');
    const storedUser = localStorage.getItem('contractor_user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setIsLoggedIn(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  // Load dashboard when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      loadDashboard();
    }
  }, [isLoggedIn, token]);

  const loadDashboard = useCallback(async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const data = await api.getDashboard(token);
      setDashboard(data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
      toast.error('Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const loadJobs = useCallback(async (status = '') => {
    if (!token) return;
    try {
      const jobs = await api.getJobs(token, status);
      setAllJobs(jobs);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      toast.error('Failed to load jobs');
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'jobs' && token) {
      loadJobs(jobFilter);
    }
  }, [activeTab, jobFilter, token, loadJobs]);

  // Filter jobs based on selected sub-tab
  const filterJobsByTab = useCallback((jobs, tab) => {
    if (!jobs || jobs.length === 0) return [];
    
    switch (tab) {
      case "reactive":
        return jobs.filter(j =>
          j.priority === "High" || 
          j.priority === "Urgent" ||
          j.job_type === "Reactive" ||
          j.job_type?.toLowerCase().includes("reactive") ||
          j.job_type?.toLowerCase().includes("emergency")
        );

      case "ppm":
        return jobs.filter(j =>
          j.job_type?.includes("PPM") ||
          j.job_type?.includes("EICR") ||
          j.job_type?.includes("Gas") ||
          j.job_type?.toLowerCase().includes("planned") ||
          j.job_type?.toLowerCase().includes("maintenance") ||
          j.job_type?.toLowerCase().includes("inspection")
        );

      case "quoted":
        return jobs.filter(j =>
          ["Awaiting Quote", "Quote Submitted", "Approved", "Quote Required"].includes(j.contractor_status) ||
          ["Awaiting Quote", "Quote Submitted", "Approved", "Quote Required"].includes(j.status)
        );

      case "scheduled":
        return jobs.filter(j =>
          ["Scheduled", "In Progress", "Accepted"].includes(j.contractor_status) ||
          ["Scheduled", "In Progress", "Accepted"].includes(j.status) ||
          j.scheduled_visit_date
        );

      case "action":
        return jobs.filter(j => {
          const missingPhotos = !j.has_before_photo || !j.has_during_photo || !j.has_after_photo;
          const missingCert = j.requires_certificate && !j.certificate_uploaded;
          const missingRams = !j.rams_accepted;
          return missingPhotos || missingCert || missingRams;
        });

      case "invoicing":
        return jobs.filter(j =>
          ["Completed", "Invoice Submitted", "Work Complete", "Awaiting Invoice"].includes(j.contractor_status) ||
          ["Completed", "Invoice Submitted", "Work Complete", "Awaiting Invoice"].includes(j.status)
        );

      default:
        return jobs;
    }
  }, []);

  // Get filtered jobs for display
  const filteredJobs = filterJobsByTab(allJobs, jobsSubTab);

  // Calculate tab counts
  const getTabCounts = useCallback((jobs) => {
    return {
      reactive: filterJobsByTab(jobs, 'reactive').length,
      ppm: filterJobsByTab(jobs, 'ppm').length,
      quoted: filterJobsByTab(jobs, 'quoted').length,
      scheduled: filterJobsByTab(jobs, 'scheduled').length,
      action: filterJobsByTab(jobs, 'action').length,
      invoicing: filterJobsByTab(jobs, 'invoicing').length,
    };
  }, [filterJobsByTab]);

  const tabCounts = getTabCounts(allJobs);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await api.login(credentials.email, credentials.password);
      localStorage.setItem('contractor_token', res.token);
      localStorage.setItem('contractor_user', JSON.stringify(res.user));
      setToken(res.token);
      setUser(res.user);
      setIsLoggedIn(true);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('contractor_token');
    localStorage.removeItem('contractor_user');
    setIsLoggedIn(false);
    setUser(null);
    setToken(null);
    setDashboard(null);
    setSelectedJob(null);
  };

  const openJob = async (job) => {
    try {
      console.log('Opening job:', job.id);
      const data = await api.getJob(token, job.id);
      console.log('Job data received:', data);
      setSelectedJob(data);
    } catch (err) {
      console.error('Failed to load job:', err);
      // Handle archived job (410 Gone) error
      if (err.response?.status === 410) {
        const errorData = err.response?.data?.detail;
        toast.error(errorData?.message || 'This job has been archived and is no longer accessible');
        // Refresh the dashboard to remove the archived job from the list
        loadDashboard();
        if (activeTab === 'jobs') {
          loadJobs(jobFilter);
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to load job details');
      }
    }
  };

  const handleStatusChange = async (job, status) => {
    try {
      await api.updateStatus(token, job.id, status);
      toast.success(`Job ${status.toLowerCase()}`);
      loadDashboard();
      if (selectedJob?.job?.id === job.id) {
        const refreshed = await api.getJob(token, job.id);
        setSelectedJob(refreshed);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleSchedule = async (value) => {
    if (!selectedJob?.job?.id) return;
    await api.updateSchedule(token, selectedJob.job.id, value, true);
    const refreshed = await api.getJob(token, selectedJob.job.id);
    setSelectedJob(refreshed);
    loadDashboard();
  };

  const handleAddNote = async (message, noteType) => {
    if (!selectedJob?.job?.id) return;
    await api.addNote(token, selectedJob.job.id, message, noteType);
    const refreshed = await api.getJob(token, selectedJob.job.id);
    setSelectedJob(refreshed);
    loadDashboard();
  };

  const handleUpload = async (file, category, caption) => {
    if (!selectedJob?.job?.id) return;
    await api.uploadFile(token, selectedJob.job.id, file, category, caption);
    const refreshed = await api.getJob(token, selectedJob.job.id);
    setSelectedJob(refreshed);
    loadDashboard();
  };

  const handleInvoice = async (invoiceData) => {
    if (!selectedJob?.job?.id) return;
    try {
      const result = await api.submitInvoice(token, selectedJob.job.id, invoiceData);
      if (result.ok) {
        toast.success('Invoice submitted successfully!');
        const refreshed = await api.getJob(token, selectedJob.job.id);
        setSelectedJob(refreshed);
        loadDashboard();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit invoice');
      throw err;
    }
  };

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1F7A78] to-[#005456] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-2">
            <img 
              src="/phs-logo.png" 
              alt="Park Home Support" 
              className="h-24 mx-auto mb-4"
            />
            <CardTitle className="text-[#1F7A78] text-2xl">Contractor Portal</CardTitle>
            <p className="text-stone-500 text-sm">Sign in to manage your assigned jobs</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  placeholder="contractor@example.com"
                  required
                  className="rounded-xl"
                  data-testid="contractor-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Enter your password"
                  required
                  className="rounded-xl"
                  data-testid="contractor-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#1F7A78] hover:bg-[#005456] rounded-xl h-11"
                disabled={loginLoading}
                data-testid="contractor-login-btn"
              >
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1F7A78] to-[#005456] text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/phs-logo.png" 
              alt="Park Home Support" 
              className="h-10"
            />
            <div className="hidden sm:block">
              <h1 className="font-semibold">Contractor Portal</h1>
              <p className="text-sm text-white/80">{user?.name || user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={loadDashboard}
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 -mb-px">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Home },
              { id: 'jobs', label: 'My Jobs', icon: Briefcase },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedJob(null); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-white text-[#1F7A78]' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F7A78] mx-auto"></div>
            <p className="mt-4 text-slate-500">Loading...</p>
          </div>
        ) : activeTab === 'dashboard' ? (
          <>
            {/* Dashboard Title */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Welcome back, {user?.name || 'Contractor'}</h2>
              <p className="text-slate-500">Here's an overview of your jobs and tasks</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard 
                title="New Requests" 
                value={dashboard?.kpis?.new_requests || 0} 
                icon={AlertTriangle} 
                tone="warn" 
              />
              <StatCard 
                title="Active Jobs" 
                value={dashboard?.kpis?.active_jobs || 0} 
                icon={Wrench} 
                tone="active" 
              />
              <StatCard 
                title="Completed" 
                value={dashboard?.kpis?.completed_jobs || 0} 
                icon={CheckCircle2} 
                tone="done" 
              />
              <StatCard 
                title="Overdue" 
                value={dashboard?.kpis?.overdue_jobs || 0} 
                icon={Clock} 
                tone="urgent" 
              />
            </div>

            {/* Main Grid */}
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              {/* Left Column - Job Lists */}
              <div className="space-y-6">
                {/* Today's Visits */}
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[#1F7A78]" />
                    Today's Visits
                  </h3>
                  {dashboard?.today_visits?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.today_visits.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onOpen={openJob}
                          onAccept={(j) => handleStatusChange(j, 'Accepted')}
                          onDecline={(j) => handleStatusChange(j, 'Declined')}
                          onComplete={(j) => handleStatusChange(j, 'Completed')}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center text-slate-500">
                        <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No visits scheduled for today
                      </CardContent>
                    </Card>
                  )}
                </section>

                {/* New Requests */}
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    New Job Requests
                  </h3>
                  {dashboard?.new_requests?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.new_requests.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onOpen={openJob}
                          onAccept={(j) => handleStatusChange(j, 'Accepted')}
                          onDecline={(j) => handleStatusChange(j, 'Declined')}
                          onComplete={(j) => handleStatusChange(j, 'Completed')}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center text-slate-500">
                        <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No new requests
                      </CardContent>
                    </Card>
                  )}
                </section>

                {/* Active Jobs */}
                <section>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-blue-500" />
                    Active Jobs
                  </h3>
                  {dashboard?.active_jobs?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.active_jobs.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          onOpen={openJob}
                          onAccept={(j) => handleStatusChange(j, 'Accepted')}
                          onDecline={(j) => handleStatusChange(j, 'Declined')}
                          onComplete={(j) => handleStatusChange(j, 'Completed')}
                        />
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed">
                      <CardContent className="py-8 text-center text-slate-500">
                        <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        No active jobs
                      </CardContent>
                    </Card>
                  )}
                </section>
              </div>

              {/* Right Column - Selected Job or Placeholder (desktop only) */}
              <div className="hidden xl:block xl:sticky xl:top-24 xl:self-start">
                {selectedJob ? (
                  <JobDetailPanel
                    job={selectedJob.job}
                    notes={selectedJob.notes || []}
                    files={selectedJob.files || []}
                    updates={selectedJob.updates || []}
                    onStatusChange={(status) => handleStatusChange(selectedJob.job, status)}
                    onSchedule={handleSchedule}
                    onAddNote={handleAddNote}
                    onUpload={handleUpload}
                    onInvoice={handleInvoice}
                    onClose={() => setSelectedJob(null)}
                  />
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="py-16 text-center">
                      <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">Select a job to view details</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Click "View Job" on any card to see notes, uploads, and actions
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </>
        ) : activeTab === 'jobs' ? (
          <>
            {/* Jobs Tab - New Structure */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">My Jobs</h2>
              <p className="text-slate-500">Organized by work type and status</p>
            </div>

            {/* New Tab Structure */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 scrollbar-hide" data-testid="job-filter-tabs">
              {JOB_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setJobsSubTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap font-medium text-sm transition-all ${
                    jobsSubTab === tab.key
                      ? "bg-[#1F7A78] text-white shadow-lg"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <span>{tab.label}</span>
                  {tabCounts[tab.key] > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      jobsSubTab === tab.key 
                        ? "bg-white/20 text-white" 
                        : tab.key === 'action' && tabCounts[tab.key] > 0
                          ? "bg-red-100 text-red-700"
                          : "bg-slate-200 text-slate-600"
                    }`}>
                      {tabCounts[tab.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Jobs Grid */}
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-3">
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job) => (
                    <div 
                      key={job.id} 
                      className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 hover:border-[#1F7A78]/30 hover:shadow-md transition-all"
                      data-testid={`job-card-enhanced-${job.id}`}
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-slate-900">{job.site_name || job.park_name || job.property_address || 'Unknown Site'}</div>
                        <div className="text-xs bg-slate-100 px-2.5 py-1 rounded-lg font-mono text-slate-500">
                          {job.job_number || job.id}
                        </div>
                      </div>

                      {/* Address */}
                      <div className="flex items-center gap-1 text-sm text-slate-600 mb-2">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{job.property_address || job.full_address || `${job.site_name || ''} ${job.plot_number ? `- Plot ${job.plot_number}` : ''}`}</span>
                      </div>

                      {/* Description */}
                      {(job.scope_summary || job.description) && (
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {job.scope_summary || job.description}
                        </p>
                      )}

                      {/* Status + Due Date Row */}
                      <div className="flex justify-between items-center mt-2 mb-3">
                        <Badge className={`${
                          job.contractor_status === 'Completed' ? 'bg-green-100 text-green-800 border-green-200' :
                          job.contractor_status === 'In Progress' ? 'bg-cyan-100 text-cyan-800 border-cyan-200' :
                          job.contractor_status === 'Scheduled' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                          job.contractor_status === 'New Request' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                          'bg-slate-100 text-slate-800 border-slate-200'
                        } border`}>
                          {job.contractor_status || job.status || 'New Request'}
                        </Badge>
                        {(job.due_date || job.response_due_date) && (
                          <span className="text-sm font-medium text-red-600">
                            Due: {new Date(job.due_date || job.response_due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                      </div>

                      {/* Action Flags */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(!job.has_before_photo || !job.has_during_photo || !job.has_after_photo) && job.contractor_status !== 'Completed' && (
                          <span className="bg-red-100 text-red-700 px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                            <Camera className="h-3 w-3" />
                            Photos Required
                          </span>
                        )}
                        {job.requires_certificate && !job.certificate_uploaded && job.contractor_status !== 'Completed' && (
                          <span className="bg-yellow-100 text-yellow-700 px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            Cert Required
                          </span>
                        )}
                        {!job.rams_accepted && job.contractor_status !== 'Completed' && job.contractor_status !== 'New Request' && (
                          <span className="bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            RAMS Required
                          </span>
                        )}
                        {job.priority === 'High' || job.priority === 'Urgent' ? (
                          <span className="bg-red-500 text-white px-2.5 py-1 rounded-lg text-xs font-bold">
                            {job.priority}
                          </span>
                        ) : null}
                      </div>

                      {/* View Job Button */}
                      <Button
                        onClick={() => openJob(job)}
                        className="w-full bg-[#1F7A78] hover:bg-[#005456] text-white"
                        data-testid={`view-job-btn-${job.id}`}
                      >
                        View Job
                      </Button>
                    </div>
                  ))
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="py-12 text-center text-slate-500">
                      <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">No jobs in this category</p>
                      <p className="text-sm text-slate-400 mt-1">
                        {jobsSubTab === 'reactive' && 'No high-priority or reactive jobs'}
                        {jobsSubTab === 'ppm' && 'No planned maintenance jobs'}
                        {jobsSubTab === 'quoted' && 'No jobs awaiting quotes'}
                        {jobsSubTab === 'scheduled' && 'No scheduled jobs for today'}
                        {jobsSubTab === 'action' && '✅ All actions complete!'}
                        {jobsSubTab === 'invoicing' && 'No completed jobs awaiting invoice'}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Right Column - Selected Job (desktop only) */}
              <div className="hidden xl:block xl:sticky xl:top-24 xl:self-start">
                {selectedJob ? (
                  <JobDetailPanel
                    job={selectedJob.job}
                    notes={selectedJob.notes || []}
                    files={selectedJob.files || []}
                    updates={selectedJob.updates || []}
                    onStatusChange={(status) => handleStatusChange(selectedJob.job, status)}
                    onSchedule={handleSchedule}
                    onAddNote={handleAddNote}
                    onUpload={handleUpload}
                    onInvoice={handleInvoice}
                    onClose={() => setSelectedJob(null)}
                  />
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="py-16 text-center">
                      <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">Select a job to view details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </>
        ) : null}
      </main>

      {/* Mobile Job Detail Dialog - Only render on mobile screens */}
      {isMobile && (
        <Dialog open={!!selectedJob} onOpenChange={(open) => !open && setSelectedJob(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader className="p-4 border-b bg-gradient-to-r from-[#1F7A78] to-[#005456] text-white rounded-t-lg">
              <DialogTitle className="text-lg font-semibold">
                {selectedJob?.job?.job_reference || 'Job Details'}
              </DialogTitle>
            </DialogHeader>
            {selectedJob && (
              <div className="p-4">
                <JobDetailPanel
                  job={selectedJob.job}
                  notes={selectedJob.notes || []}
                  files={selectedJob.files || []}
                  updates={selectedJob.updates || []}
                  onStatusChange={(status) => handleStatusChange(selectedJob.job, status)}
                  onSchedule={handleSchedule}
                  onAddNote={handleAddNote}
                  onUpload={handleUpload}
                  onInvoice={handleInvoice}
                  onClose={() => setSelectedJob(null)}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Footer */}
      <footer className="bg-white border-t mt-auto py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          Leisure Home Solutions Ltd | admin@lhsportal.uk | 07557 017694
        </div>
      </footer>
    </div>
  );
}
